import React, { forwardRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Button
} from '@mui/material';

/**
 * InlineImproveInput - A reusable component for inline improvement prompts
 *
 * @param {Object} props
 * @param {string} props.improvePrompt - The current improvement prompt text
 * @param {Function} props.onPromptChange - Handler for when the prompt text changes
 * @param {Function} props.onSend - Handler for when the send button is clicked
 * @param {Function} props.onCancel - Handler for when the cancel button is clicked
 * @param {string} props.placeholder - Optional placeholder text for the input
 * @param {string} props.promptText - Optional text to display above the input
 * @param {React.Ref} ref - Forwarded ref for scrolling to this component
 */
const InlineImproveInput = forwardRef(({
  improvePrompt,
  onPromptChange,
  onSend,
  onCancel,
  placeholder = "Describe the improvements you'd like...",
  promptText = "How would you like to improve this?"
}, ref) => {
  return (
    <Box
      ref={ref}
      sx={{
        mt: 2,
        p: 2,
        borderRadius: 1,
      }}>
      <Typography variant="body2" sx={{ mb: 1 }}>
        {promptText}
      </Typography>
      <TextField
        multiline
        fullWidth
        value={improvePrompt}
        onChange={(e) => onPromptChange(e.target.value)}
        placeholder={placeholder}
        variant="outlined"
        minRows={3}
        sx={{
          mb: 1,
          '& .MuiOutlinedInput-root': {
            borderRadius: '4px',
            color: 'var(--color-blue, #475969)',
            transition: 'all 0.2s',
            '&:hover .MuiOutlinedInput-notchedOutline': {
              borderColor: '#475969',
              borderWidth: '1px',
            },
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: '#475969',
              borderWidth: '1px',
            }
          },
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#4759694d',
            border: '1px solid #4759694d',
            borderRadius: '4px',
          },
          '& .MuiInputBase-input': {
            fontSize: '14px',
            padding: '12px 14px',
            color: 'var(--color-blue, #475969)',
          },
          '& .MuiInputBase-input::placeholder': {
            color: '#a2abb3',
            opacity: 0.7,
            fontSize: '14px',
          }
        }}
      />
      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button
          size="small"
          onClick={onCancel}
        >
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={onSend}
        >
          Send
        </Button>
      </Box>
    </Box>
  );
});

// Add display name for debugging
InlineImproveInput.displayName = 'InlineImproveInput';

export default InlineImproveInput;